import { useState } from 'react';
import { line, Color, GEOLOCATION_REFUSAL_KEYS, Size, formatDateTime, t, zoneAbbreviation, type WorkOrderOnSiteAnswer, type WorkOrderOnSiteRequest, type WorkOrderOnSiteWorker } from '@bms2/shared';
import { AlertBanner, Button, PasswordInput, RadioGroup } from '../../components/controls';
import { DetailList, DetailRow } from '../../components/composites/DetailList';
import { Modal } from '../../components/modals/Modal';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { post, useApi } from './api';
import { readPosition, type GeolocationReader, type PositionAnswer, browserGeolocation } from './geolocation';
import styles from './RecordPage.module.css';

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1). */
export const workOrderOnSitePath = (workOrderId: string, action?: 'check-in' | 'check-out'): string => `/work/on-site/${workOrderId}${action ? `/${action}` : ''}`;

interface GateAsk {
  worker: WorkOrderOnSiteWorker;
  action: 'check-in' | 'check-out';
  deviceId: string;
  secret: string;
}

/**
 * Who is on site (piece 29, T309; decision 0873, amending 0229): the job's
 * assigned workers, each with Check in, or Check out and the arrival in the
 * viewer's zone. 🔒 At an ungated site the press writes the check-in alone; at
 * a gated one it opens the gate's own sign-in, the device and the worker's
 * clock PIN (decision 0150). 🔒 The browser is asked where the viewer is only
 * when the viewer checks in their own employment (decision 0675); a desk
 * checking in somebody else sends nothing, and the row reads no location
 * shared. A refusal — one open visit per employment above all — is shown.
 * 🔒 The viewer's own check-in then says whether a location was recorded, and
 * where none was, names the cause through the shared refusal keys (decision
 * 3440; T2314): the check-in stands, the page says why it carries no point.
 */
export function WorkOrderOnSite({ workOrderId, apiBaseUrl = '', onChanged, geolocation = browserGeolocation() }: { workOrderId: string; apiBaseUrl?: string; onChanged: () => void; geolocation?: GeolocationReader | null }) {
  const panel = useApi<WorkOrderOnSiteAnswer>(workOrderOnSitePath(workOrderId), apiBaseUrl);
  const zone = useViewerZone();
  const dateFormat = useDateFormat();
  const [refusal, setRefusal] = useState<string | null>(null);
  const [gateAsk, setGateAsk] = useState<GateAsk | null>(null);
  const [located, setLocated] = useState<{ tone: 'good' | 'warning'; message: string } | null>(null);
  const answer = panel.state.kind === 'ready' ? panel.state.data : null;
  if (!answer) return null;
  const workers = Array.isArray(answer.workers) ? answer.workers : [];
  const devices = Array.isArray(answer.devices) ? answer.devices : [];

  const send = async (action: 'check-in' | 'check-out', body: WorkOrderOnSiteRequest) => {
    setRefusal(null);
    const wrote = await post(workOrderOnSitePath(workOrderId, action), body, apiBaseUrl);
    if (!wrote.ok) {
      setRefusal(line(t('workOrder.onSite.refused'), wrote.message));
      return false;
    }
    panel.reload?.();
    onChanged();
    return true;
  };
  /** What the viewer's own check-in says about the point it carried: recorded, or the cause none was. */
  const sayLocation = (position: PositionAnswer | null) => {
    if (!position) return;
    setLocated(position.ok ? { tone: 'good', message: t('workOrder.onSite.withLocation') } : { tone: 'warning', message: `${t('checkIn.noCoordinates')} ${t(GEOLOCATION_REFUSAL_KEYS[position.refusal])}` });
  };
  const press = async (worker: WorkOrderOnSiteWorker, action: 'check-in' | 'check-out') => {
    setLocated(null);
    if (answer.gated) return setGateAsk({ worker, action, deviceId: devices.length === 1 ? devices[0]!.id : '', secret: '' });
    if (action === 'check-out') return void send('check-out', { employmentId: worker.employmentId });
    // 🔒 Only the viewer's own check-in asks the browser; a desk's point proves nothing about somebody else (decision 0675).
    const position = worker.isViewer ? await readPosition(geolocation) : null;
    const done = await send('check-in', { employmentId: worker.employmentId, ...(position?.ok ? position.point : {}) });
    if (done) sayLocation(position);
  };
  const signAtGate = async () => {
    if (!gateAsk) return;
    const position = gateAsk.action === 'check-in' && gateAsk.worker.isViewer ? await readPosition(geolocation) : null;
    const done = await send(gateAsk.action, { employmentId: gateAsk.worker.employmentId, deviceId: gateAsk.deviceId, secret: gateAsk.secret, ...(position?.ok ? position.point : {}) });
    if (done) {
      setGateAsk(null);
      sayLocation(position);
    }
  };
  const state = (worker: WorkOrderOnSiteWorker): string => {
    if (!worker.open) return t('workOrder.onSite.notIn');
    if (!worker.open.here) return line(t('workOrder.onSite.elsewhere'), worker.open.siteName);
    return line(t('workOrder.onSite.in'), formatDateTime(worker.open.checkedInAt, zone, dateFormat), zoneAbbreviation(worker.open.checkedInAt, zone), worker.open.hasLocation ? null : `· ${t('workOrder.onSite.noLocation')}`);
  };

  return (
    <section data-work-order-on-site-panel={workOrderId} data-on-site-gated={answer.gated ? 'true' : 'false'}>
      <h3 className={styles.fieldLabel}>{t('workOrder.onSite.title')}</h3>
      {refusal ? <AlertBanner tone="error" message={refusal} action={{ label: t('modal.dismiss'), onAction: () => setRefusal(null) }} /> : null}
      {located ? <AlertBanner tone={located.tone} message={located.message} action={{ label: t('modal.dismiss'), onAction: () => setLocated(null) }} /> : null}
      {workers.length === 0 ? <p className={styles.hint} data-on-site-none="true">{t('workOrder.onSite.none')}</p> : null}
      <DetailList look="separator">
        {workers.map((worker) => (
          <DetailRow
            key={worker.employmentId}
            data-on-site-worker={worker.employmentId}
            title={worker.name}
            detail={state(worker)}
            actions={worker.open?.here
              ? <Button size={Size.Small} text={[t('workOrder.onSite.checkOut')]} actions={[() => void press(worker, 'check-out')]} />
              : <Button size={Size.Small} color={Color.Navy} text={[t('workOrder.onSite.checkIn')]} actions={[() => void press(worker, 'check-in')]} />}
          />
        ))}
      </DetailList>
      {gateAsk ? (
        <Modal
          open
          title={t('workOrder.onSite.gateTitle')}
          subtitle={gateAsk.worker.name}
          onCancel={() => setGateAsk(null)}
          dataModal="work-order-gate"
          actions={[
            { kind: 'primary', label: gateAsk.action === 'check-in' ? t('workOrder.onSite.checkIn') : t('workOrder.onSite.checkOut'), onAct: () => void signAtGate(), disabled: !gateAsk.deviceId || !gateAsk.secret },
            { kind: 'cancel', label: t('modal.cancel'), onAct: () => setGateAsk(null) },
          ]}
        >
          {refusal ? <AlertBanner tone="error" message={refusal} action={{ label: t('modal.dismiss'), onAction: () => setRefusal(null) }} /> : null}
          <p className={styles.hint}>{t('workOrder.onSite.gateHint')}</p>
          {devices.length === 0 ? <p className={styles.problem} data-on-site-no-device="true">{t('workOrder.onSite.noDevice')}</p> : (
            <RadioGroup name="gate-device" legend={t('workOrder.onSite.device')} options={devices.map((device) => ({ value: device.id, label: device.label }))} value={gateAsk.deviceId || null} onChange={(deviceId) => setGateAsk({ ...gateAsk, deviceId })} />
          )}
          <PasswordInput size={Size.Small} label={t('workOrder.onSite.pin')} value={gateAsk.secret} onChange={(secret) => setGateAsk({ ...gateAsk, secret })} purpose="login" name="gate-pin" required />
        </Modal>
      ) : null}
    </section>
  );
}
